import { createStyles, Group, JsonInput, Modal, NumberInput, rem, Stack, TextInput, Tooltip } from '@mantine/core';
import {
  CopyableFieldCheckbox,
  HiddenFieldCheckbox,
  IndexedFieldCheckbox,
  ReadOnlyFieldCheckbox,
  RequiredFieldCheckbox,
} from 'pages/revisions-module/template-editor/editors/form/configurator/InputCheckboxes';
import { FormEventHandler, useCallback, useEffect, useState } from 'react';
import ActionIcon from 'components/ActionIcon';
import Button from 'components/Button';
import {
  IFormInputContext,
  IFormInputSpec,
  IFormInputType,
} from 'pages/revisions-module/template-editor/editors/form/types';
import { useForm } from '@mantine/form';
import InputNameDisplay from 'pages/revisions-module/template-editor/editors/form/configurator/InputNameDisplay';
import createValidator from 'components/forms/validators/create-validator';
import required from 'components/forms/validators/rules/rule-required';
import InputTypeSelect from 'pages/revisions-module/template-editor/editors/form/configurator/InputTypeSelect';
import InputPropertiesSubForm from 'pages/revisions-module/template-editor/editors/form/configurator/InputPropertiesSubForm';
import { useFormInputs } from 'pages/revisions-module/template-editor/editors/form/input/FormInputsDataProvider';
import P4Regular from 'components/typography/P4Regular';
import { IconCode, IconListDetails } from '@tabler/icons-react';
import { FEATURE_TOGGLE_CODE_EDITOR_IN_INPUT_CONFIGURATOR } from 'env';

const useStyles = createStyles((theme) => ({
  wrapper: {
    borderTop: `${rem(1)} solid ${theme.colors.gray[1]}`,
  },
  section: {
    borderBottom: `${rem(1)} solid ${theme.colors.gray[1]}`,
  },
}));

/**
 * Parameters of the InputConfigurator component.
 */
export interface InputConfiguratorProps {
  opened: boolean;
  primaryButtonLabel?: string;
  readOnly?: Partial<Record<keyof IFormInputSpec, boolean>>;
  hidden?: Partial<Record<keyof IFormInputSpec, boolean>>;
  initialValues?: IFormInputSpec;
  inputTypes?: Partial<Record<IFormInputType, boolean>>;
  context?: IFormInputContext;
  onClose: () => void;
  onSubmit: (spec: IFormInputSpec) => void;
}

/**
 * Used to create and edit inputs in a form.
 */
export default function InputConfigurator({
  opened,
  primaryButtonLabel = 'Uložiť',
  readOnly = {},
  hidden = {},
  initialValues = {
    label: '',
    name: '',
    required: false,
    copyable: true,
    readOnly: false,
    hidden: false,
    index: false,
    type: 'text',
  },
  inputTypes,
  context = 'revision',
  onClose,
  onSubmit,
}: InputConfiguratorProps) {
  const { classes } = useStyles();
  const { generateInputName } = useFormInputs();
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<'visual' | 'code'>('visual');
  const [codeJson, setCodeJson] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);

  const form = useForm<IFormInputSpec>({
    initialValues,
    validate: {
      label: createValidator([required]),
    },
  });

  const submit = useCallback(
    (spec: IFormInputSpec) => {
      setSubmitting(true);
      onSubmit(spec);
      onClose();
    },
    [onSubmit, onClose]
  );

  const emptySubmit: FormEventHandler<HTMLFormElement> = useCallback((e) => e.preventDefault(), []);

  /**
   * Merges a parsed spec with the current form values for every field flagged
   * as readOnly, so that the user cannot overwrite protected fields via the
   * code editor.
   */
  const applyReadOnly = useCallback(
    (parsed: IFormInputSpec): IFormInputSpec => {
      const protected_ = (Object.keys(readOnly) as (keyof IFormInputSpec)[]).filter((k) => readOnly[k]);

      if (protected_.length === 0) {
        return parsed;
      }

      return protected_.reduce((acc, key) => ({ ...acc, [key]: form.values[key] }), parsed);
    },
    [readOnly, form.values]
  );

  /** Switches between visual and code editor modes, syncing values in both directions. */
  const toggleMode = useCallback(() => {
    if (mode === 'visual') {
      setCodeJson(JSON.stringify(form.values, null, 2));
      setCodeError(null);
      setMode('code');
    } else {
      try {
        const parsed = applyReadOnly(JSON.parse(codeJson) as IFormInputSpec);
        form.setValues(parsed);
        setCodeError(null);
        setMode('visual');
      } catch {
        setCodeError('Neplatný JSON');
      }
    }
  }, [mode, form, codeJson, applyReadOnly]);

  /** Parses the JSON from the code editor and submits it directly. */
  const submitCode: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();
      if (submitting) return;
      try {
        const parsed = applyReadOnly(JSON.parse(codeJson) as IFormInputSpec);
        setCodeError(null);
        setSubmitting(true);
        onSubmit(parsed);
        onClose();
      } catch {
        setCodeError('Neplatný JSON');
      }
    },
    [codeJson, submitting, onSubmit, onClose, applyReadOnly]
  );

  useEffect(() => {
    if (opened) {
      form.reset();
      form.setValues(initialValues);
      setSubmitting(false);
      setMode('visual');
      setCodeError(null);
    }
  }, [opened]);

  useEffect(() => {
    if (!readOnly.name) {
      form.setFieldValue('name', generateInputName(form.values.label));
    }
  }, [form.values.label, readOnly.name]);

  useEffect(() => {
    if (
      form.values.type === 'constant' ||
      form.values.type === 'valueMap' ||
      form.values.type === 'ifElse' ||
      form.values.type === 'comparator' ||
      form.values.type === 'textExpression' ||
      form.values.type === 'fieldUpdatedAt'
    ) {
      if (!form.values.readOnly) {
        form.setFieldValue('readOnly', true);
      }

      if (form.values.copyable) {
        form.setFieldValue('copyable', false);
      }
    }
  }, [form.values.type, form.values.readOnly, form.values.copyable]);

  const isDirty = form.isDirty() || mode === 'code';

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Group spacing={8} align="center">
          <span>Konfigurátor poľa</span>
          {FEATURE_TOGGLE_CODE_EDITOR_IN_INPUT_CONFIGURATOR && (
            <Tooltip label={mode === 'visual' ? 'Kódový editor' : 'Vizuálny editor'} withArrow={false} openDelay={200}>
              <ActionIcon size="sm" variant="subtle-gray" onClick={toggleMode}>
                {mode === 'visual' ? <IconCode size={16} /> : <IconListDetails size={16} />}
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      }
      centered
      closeOnClickOutside={!isDirty}
      closeOnEscape={!isDirty}
      size={600}
    >
      <form onSubmit={mode === 'code' ? submitCode : submitting ? emptySubmit : form.onSubmit(submit)}>
        <Stack spacing={16} pt={16} className={classes.wrapper}>
          {mode === 'code' ? (
            <JsonInput
              value={codeJson}
              onChange={setCodeJson}
              formatOnBlur
              autosize
              minRows={18}
              validationError={codeError ?? 'Neplatný JSON'}
              size="sm"
              styles={{ input: { fontFamily: 'monospace', fontSize: rem(12) } }}
            />
          ) : (
            <Stack spacing={0}>
              <Stack spacing={12} pb={16} className={classes.section}>
                <TextInput label="Názov poľa" size="lg" data-autofocus required {...form.getInputProps('label')} />
                <InputNameDisplay name={form.values.name} />
                <Group noWrap spacing={12} sx={{ ':empty': { display: 'none' } }}>
                  {!hidden.minWidth && (
                    <NumberInput
                      size="sm"
                      label="Minimálna šírka"
                      rightSection={<P4Regular>px</P4Regular>}
                      {...form.getInputProps('minWidth')}
                    />
                  )}
                  {!hidden.maxWidth && (
                    <NumberInput
                      size="sm"
                      label="Maximálna šírka"
                      rightSection={<P4Regular>px</P4Regular>}
                      {...form.getInputProps('maxWidth')}
                    />
                  )}
                </Group>
              </Stack>
              <Stack spacing={8} py={16} className={classes.section}>
                <InputTypeSelect readOnly={readOnly.type} inputTypes={inputTypes} {...form.getInputProps('type')} />
                <Stack spacing={12} bg="red.0" p={12} sx={{ borderRadius: rem(4), ':empty': { display: 'none' } }}>
                  <InputPropertiesSubForm type={form.values.type} form={form} context={context} />
                </Stack>
              </Stack>
              <Stack spacing={12} py={16} className={classes.section}>
                {!hidden.required && <RequiredFieldCheckbox {...form.getInputProps('required')} />}
                {!hidden.copyable && <CopyableFieldCheckbox {...form.getInputProps('copyable')} />}
                {!hidden.readOnly && <ReadOnlyFieldCheckbox {...form.getInputProps('readOnly')} />}
                {!hidden.hidden && <HiddenFieldCheckbox {...form.getInputProps('hidden')} />}
                {!hidden.index && context === 'revision' && <IndexedFieldCheckbox {...form.getInputProps('index')} />}
              </Stack>
            </Stack>
          )}
          <Group position="apart">
            <Button type="button" variant="subtle-gray" size="md" onClick={onClose}>
              Zrušiť
            </Button>
            <Button type="submit" variant="primary" size="md">
              {primaryButtonLabel}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
